/**
 * qualityGateErrors — maps pre-evaluation quality gate failure messages to
 * plain-language user guidance.
 *
 * The backend raises VideoQualityError with a message like:
 *   "Video did not pass quality checks: <check1 message>; <check2 message>"
 *
 * Each check message contains keywords we can pattern-match to surface
 * actionable guidance. We intentionally match on the check name prefix
 * (from PreEvalCheck.name) and stable message patterns rather than the
 * full string so that wording tweaks on the backend don't break mapping.
 */

export interface QualityErrorInfo {
  /** Short, plain-language description shown prominently. */
  headline: string;
  /** Optional extra coaching tip shown below the headline. */
  tip?: string;
  /** Whether the "Try again" flow should re-show the capture guidance card. */
  showGuidance: boolean;
}

/**
 * Detect whether a job error_message looks like a pre-eval quality gate failure.
 * Returns true when the message contains the gate-failure prefix.
 */
export function isQualityGateFailure(errorMessage: string): boolean {
  return errorMessage.toLowerCase().includes('video did not pass quality checks');
}

const PATTERNS: Array<{
  test: (msg: string) => boolean;
  info: QualityErrorInfo;
}> = [
  // Duration — too short
  {
    test: (msg) => /too short/i.test(msg) || /minimum required duration/i.test(msg),
    info: {
      headline: 'Clip too short — record a longer take',
      tip: 'Aim for at least 1–2 seconds of the full movement.',
      showGuidance: true,
    },
  },
  // Duration — too long
  {
    test: (msg) => /too long|maximum allowed duration/i.test(msg),
    info: {
      headline: 'Clip too long — trim before uploading',
      tip: 'Use the "Trim clip" option below to cut to just the movement.',
      showGuidance: false,
    },
  },
  // Corrupted / unreadable
  {
    test: (msg) =>
      /corrupted|frame rate|could not read|empty|integrity|container/i.test(msg),
    info: {
      headline: 'Video file could not be read — try recording again',
      tip: 'If the problem persists, try a different format or re-record in the camera app.',
      showGuidance: false,
    },
  },
  // Overexposed — check BEFORE dark so "Harsh lighting" in overexposed messages
  // is not incorrectly caught by the dark pattern's /lighting/ branch.
  {
    test: (msg) => /overexposed|harsh light/i.test(msg),
    info: {
      headline: 'Video overexposed — avoid harsh backlighting',
      tip: 'Step away from bright windows behind you or draw the blinds.',
      showGuidance: true,
    },
  },
  // Brightness / dark
  {
    test: (msg) => /dark|brightness|lighting|poorly lit/i.test(msg),
    info: {
      headline: 'Video too dark — film in better light',
      tip: 'Face a window or turn on room lights so the AI can see your body clearly.',
      showGuidance: true,
    },
  },
];

/**
 * Map an error_message from a failed evaluation job to user-friendly guidance.
 *
 * Returns null when the message does not look like a quality gate failure.
 * In that case callers should fall back to showing the raw error_message.
 */
export function mapQualityError(errorMessage: string): QualityErrorInfo | null {
  if (!isQualityGateFailure(errorMessage)) return null;

  // Extract the per-check messages after the prefix.
  const detail = errorMessage.replace(/video did not pass quality checks:\s*/i, '');

  for (const { test, info } of PATTERNS) {
    if (test(detail)) return info;
  }

  // Generic gate failure — no specific match
  return {
    headline: 'Video did not pass quality checks',
    tip: 'Check that your clip is well-lit, the full movement is in frame, and the file is not corrupted.',
    showGuidance: true,
  };
}
